import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from '../../utils/axiosInstance';

const IMAGE_BASE_URL = process.env.REACT_APP_Image_URL || "https://api.solarestimate.in/image/";

export default function VisitView() {
  const { id: visitId } = useParams(); // Get visitId from URL parameters
  const navigate = useNavigate();
  const [visitInfo, setVisitInfo] = useState(null);
  const [leadInfo, setLeadInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch visit and lead data
  useEffect(() => {
    if (!visitId) {
      setError('No visit ID provided');
      setLoading(false);
      return;
    }
    
    const fetchVisitData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch visit information
        const visitResponse = await axios.get(`/admin/api/lead-visit/${visitId}`);
        
        if (visitResponse.data.Status === 1) {
          setVisitInfo(visitResponse.data.visit);
          
          // If visit has lead ID, fetch lead information
          const leadId = visitResponse.data.visit?.leadId || visitResponse.data.visit?.lead?._id;
          if (leadId) {
            try {
              const leadResponse = await axios.get(`/admin/api/lead/${leadId}`);
              if (leadResponse.data.Status === 1) {
                setLeadInfo(leadResponse.data.lead);
              }
            } catch (leadErr) {
              // Don't set error for lead fetch failure, just log it
              console.error('Error fetching lead data:', leadErr);
            }
          }
        } else {
          setError(visitResponse.data.Message || 'Failed to fetch visit data');
        }
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Error fetching visit data');
      } finally {
        setLoading(false);
      }
    };

    fetchVisitData();
  }, [visitId]);
 
  // Helper for missing data
  const safe = (val, fallback = "-") => {
    if (!val) return fallback;
    if (typeof val === 'object' && val.name) return val.name;
    if (typeof val === 'string' || typeof val === 'number') return val;
    return fallback;
  };

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric' 
    });
  };

  // Format time helper
  const formatTime = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    });
  };

  if (loading) {
    return (
      <div className="container py-4">
        <div className="text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className="fw-bold">Visit Details</h2>
        <div className="d-flex align-items-center gap-2">
        <Link to="/user/visit" className="text-decoration-none text-secondary">
            &larr; LeadVisit
          </Link>
        </div>
      </div>
      <div className="row g-3">
        {/* Visit Information */}
        <div className="col-12 col-md-4">
          <div className="card p-4 rounded-3 h-100">
            <div className="fw-semibold mb-3 text-muted">Visit Information</div>
            <div className="text-muted">
              <div>Name: {safe(visitInfo?.name)}</div>
              <div>Note: {safe(visitInfo?.note)}</div>
              <div>Remark: {safe(visitInfo?.remark)}</div>
              <div>Last Updated: {formatDate(visitInfo?.updatedAt)}</div>
              {visitInfo?.selecteduser && (
                <div className="mt-2">
                  Selected User:
                  <div className="ps-2">
                    {`${safe(visitInfo.selecteduser.firstName, "")} ${safe(visitInfo.selecteduser.lastName, "")}`.trim()}
                    <div className="text-muted small">{safe(visitInfo.selecteduser.email)}</div>
                  </div>
                  <div className="ps-2">
                    <span className="text-muted small">Business: {safe(visitInfo.selecteduser.business?.name)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Lead Information */}
        <div className="col-12 col-md-4">
          <div className="card p-4 rounded-3 h-100">
            <div className="fw-semibold mb-3 text-muted">Lead Information</div>
            <div className="text-muted">
              <div>Lead Number: {safe(leadInfo?.leadNumber || leadInfo?.number)}</div>
              <div>Lead Name: {safe(leadInfo?.name)}</div>
              <div>Address: {safe(leadInfo?.address)}</div>
              <div>Remark: {safe(leadInfo?.remark)}</div>
              <div>City: {safe(leadInfo?.city?.name || leadInfo?.city)}</div>
              <div>State: {safe(leadInfo?.state?.name || leadInfo?.state)}</div>
              <div>Country: {safe(leadInfo?.country?.name || leadInfo?.country)}</div>
              <div>Deal Types: {safe(leadInfo?.dealType?.name || leadInfo?.dealType)}</div>
              <div>Deal Stage: {safe(leadInfo?.dealStage?.name || leadInfo?.dealStage)}</div>
              <div>Lead Source: {safe(leadInfo?.leadSource?.name || leadInfo?.leadSource)}</div>
              <div>Project Type: {safe(leadInfo?.projectType?.name || leadInfo?.projectType)}</div>
              <div>Lead Owner: {leadInfo?.leadOwner ?
                `${safe(leadInfo.leadOwner.firstName, "")} ${safe(leadInfo.leadOwner.lastName, "")}`.trim() : "-"
              }</div>
              <div>Created At: {formatDate(leadInfo?.createdAt)}</div>
              <div>Updated At: {formatDate(leadInfo?.updatedAt)}</div>
              <div>GST Number: {safe(leadInfo?.gst_number)}</div>
              <div>Mobile: {safe(leadInfo?.mobile)}</div>
              <div>Email: {safe(leadInfo?.email)}</div>
            </div>
          </div>
        </div>
        
        {/* Visit Images */}
        <div className="col-12 col-md-4">
          <div className="card p-4 rounded-3 h-100">
            <div className="fw-semibold mb-3 text-muted">Visit Images</div>
            <div className="row">
              {visitInfo?.images && visitInfo.images.length > 0 ? visitInfo.images.map((image, idx) => (
                <div key={idx} className="col-4 mb-2">
                  <div className="border rounded bg-light" style={{height:60, display:"flex", alignItems:"center", justifyContent:"center"}}>
                    <img
                      src={`${IMAGE_BASE_URL}${image}`}
                      alt={`Visit image ${idx + 1}`}
                      style={{maxWidth:"100%", maxHeight:"100%"}}
                      onError={e => { e.target.style.display = 'none'; e.target.nextSibling && (e.target.nextSibling.style.display = 'block'); }}
                    />
                  </div>
                </div>
              )) : (
                <div className="col-12">
                  <div className="text-muted text-center py-3">
                    No images available
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}